import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { cloneDeep } from 'lodash';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { ProductReference } from '../models/layout/product-reference';

/**
 * Owns the rxjs subjects, current-layout snapshot, and planogramItems map that
 * back LayoutService and its sister sub-services. Carved out as the first
 * step of the LayoutService split — anything that needs to read or publish
 * layout state goes through this service, not through the subjects directly.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutStateService {
  private layoutSource = new BehaviorSubject<PlanogramLayout | null>(null);
  private productsChangedSource = new BehaviorSubject<void>(undefined);
  private layoutBreakdownSource = new BehaviorSubject<Map<number, number>>(
    new Map()
  );
  private layoutDOSSource = new BehaviorSubject<Map<number, number>>(new Map());
  private layoutDFacingsSource = new BehaviorSubject<
    Map<number, { min: number; max: number }>
  >(new Map());
  private layoutCapacitySource = new BehaviorSubject<Map<number, number>>(
    new Map()
  );

  public layout$ = this.layoutSource.asObservable();
  public productsChanged$ = this.productsChangedSource.asObservable();
  public layoutBreakdown$ = this.layoutBreakdownSource.asObservable();
  public layoutDOS$ = this.layoutDOSSource.asObservable();
  public layoutDFacings$ = this.layoutDFacingsSource.asObservable();
  public layoutCapacity$ = this.layoutCapacitySource.asObservable();

  public planogramItems: Map<number, ProductReference> = new Map<
    number,
    ProductReference
  >();

  getLayoutValue(): PlanogramLayout | null {
    return this.layoutSource.value;
  }

  getCurrentLayout(deepclone: boolean = true): PlanogramLayout | null {
    return deepclone
      ? cloneDeep(this.layoutSource.value)
      : this.layoutSource.value;
  }

  publishLayout(layout: PlanogramLayout | null): void {
    this.layoutSource.next(layout);
  }

  publishProductsChanged(): void {
    this.productsChangedSource.next();
  }

  publishBreakdown(breakdown: Map<number, number>): void {
    this.layoutBreakdownSource.next(breakdown);
  }

  publishDOS(dos: Map<number, number>): void {
    this.layoutDOSSource.next(dos);
  }

  getDOSValue(): Map<number, number> {
    return this.layoutDOSSource.value;
  }

  publishDFacings(dFacings: Map<number, { min: number; max: number }>): void {
    this.layoutDFacingsSource.next(dFacings);
  }

  getDFacingsValue(): Map<number, { min: number; max: number }> {
    return this.layoutDFacingsSource.value;
  }

  publishCapacity(capacity: Map<number, number>): void {
    this.layoutCapacitySource.next(capacity);
  }

  getCapacityValue(): Map<number, number> {
    return this.layoutCapacitySource.value;
  }
}
